import React, { FC } from 'react';
import {
  Button,
  ListGroup,
  Modal,
  Overlay,
  Text,
} from '@cubeyou-team/hx-react-components';

import {
  useTrialUpgradeRequestMutation,
  TrialUpgradeNotifyHandlers,
} from '../../api/hooks/useTrialUpgradeRequestMutation';
import { TRIAL_FEATURES } from '../../api/constants';
import { FREE_TRIAL_COPY } from '../../lang/en-US';
import { BACKGROUND_IMAGE_URL } from './constants/url';

const CONTACT_KEY = 'trial-lockout-contact';
const LOGOUT_KEY = 'trial-lockout-logout';
const LOGOUT_PATH = '/logout';
const MODAL_WIDTH = '33rem';

/** The lockout blocks the host app, so the request outcome must never dismiss it. */
const keepLockoutOpen = () => undefined;

export type TrialLockoutModalProps = TrialUpgradeNotifyHandlers & {
  onNotifySuccess: (message: string) => void;
  onNotifyError: (message: string) => void;
};

export const TrialLockoutModal: FC<TrialLockoutModalProps> = ({
  onNotifySuccess,
  onNotifyError,
}) => {
  const { mutate, isLoading } = useTrialUpgradeRequestMutation(
    keepLockoutOpen,
    {
      onNotifySuccess,
      onNotifyError,
    }
  );

  const { LOCKOUT } = FREE_TRIAL_COPY;

  const handleContact = () =>
    mutate({ feature: TRIAL_FEATURES.TRIAL_QUOTA_EXHAUSTED });

  const handleLogout = () => window.location.assign(LOGOUT_PATH);

  return (
    <Overlay isOpen width={MODAL_WIDTH}>
      <Modal
        title={LOCKOUT.TITLE}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Button
                key={CONTACT_KEY}
                type="button"
                size="small"
                onClick={handleContact}
                disabled={isLoading}
                loading={isLoading}
                data-test-id={CONTACT_KEY}
              >
                {LOCKOUT.BUTTONS.CONTACT}
              </Button>,
              <Button
                key={LOGOUT_KEY}
                type="button"
                variant="outlined"
                size="small"
                onClick={handleLogout}
                data-test-id={LOGOUT_KEY}
              >
                {LOCKOUT.BUTTONS.LOGOUT}
              </Button>,
            ]}
          />
        }
        closable={false}
      >
        <div
          className="relative h-96 -m-8 bg-cover bg-center overflow-hidden flex flex-col justify-center"
          style={{ backgroundImage: `url('${BACKGROUND_IMAGE_URL}')` }}
        >
          <div
            className="absolute w-96 h-96 bg-gray-900 -left-80 top-32 transform rotate-45 translate-x-1/2 -translate-y-1/2"
            style={{ height: '432px', left: '-354px' }}
          />

          <div className="relative z-10 w-1/2 ml-8 -top-2 flex flex-col gap-4">
            <Text
              weight="bold"
              size="large"
              variant="primary"
              className="text-4xl"
              tag="h3"
            >
              {LOCKOUT.MESSAGE}
            </Text>
            <Text size="body" variant="light" className="w-1/2">
              {LOCKOUT.DESCRIPTION}
            </Text>
          </div>
        </div>
      </Modal>
    </Overlay>
  );
};
